import { useState } from "react";
import {
  ChartBar, Table as TableIcon, Users, EnvelopeSimple, FolderOpen, Gear, TerminalWindow, X,
} from "@phosphor-icons/react";
import { LegalModal } from "@/components/LegalModal";

export const NAV = [
  { id: "overview", label: "Resumen", icon: ChartBar },
  { id: "registro", label: "Registro (Sheet)", icon: TableIcon },
  { id: "proveedores", label: "Proveedores", icon: Users },
  { id: "gmail", label: "Bandeja Gmail", icon: EnvelopeSimple },
  { id: "drive", label: "Árbol de Drive", icon: FolderOpen },
  { id: "logs", label: "Log", icon: TerminalWindow },
  { id: "config", label: "Configuración", icon: Gear },
];

export function Sidebar({ active, setActive, cfg, gstatus, mobileOpen = false, setMobileOpen }) {
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalType, setLegalType] = useState("privacy");
  const isConnected = !!gstatus?.connected;

  const openLegal = (type) => {
    setLegalType(type);
    setLegalOpen(true);
  };
  const navContent = (
    <div className="flex flex-col h-full bg-slate-900 text-slate-300 select-none">
      {/* Header del Sidebar */}
      <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-slate-800 border border-slate-700/80 p-1 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
            <img src="/logo.png" alt="Facturapp Logo" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-white text-base leading-tight tracking-tight">Facturapp</div>
            <div className="text-[10px] text-slate-400 tracking-wide">by COCO&CO</div>
          </div>
        </div>

        {/* Botón cerrar en vista móvil */}
        {setMobileOpen && (
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
            aria-label="Cerrar menú de navegación"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navegación */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((n) => {
          const Icon = n.icon;
          const on = active === n.id;
          return (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => {
                setActive(n.id);
                if (setMobileOpen) setMobileOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                on
                  ? "bg-slate-800 text-white shadow-sm"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
              }`}
            >
              <Icon size={18} weight={on ? "fill" : "regular"} />
              {n.label}
            </button>
          );
        })}
      </nav>

      {/* Caja de información técnica */}
      <div className="px-3 pb-3">
        <div className="p-2.5 rounded-lg bg-slate-950/60 border border-slate-800/80 text-[11px] space-y-1.5 shadow-sm">
          <div className="flex items-center justify-between text-slate-400 font-sans">
            <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Estado</span>
            <button
              onClick={() => {
                setActive("config");
                if (setMobileOpen) setMobileOpen(false);
              }}
              title={isConnected ? "Google Workspace conectado" : "Google Workspace desconectado - Clic para configurar"}
              className="flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-slate-800/80 transition-colors"
            >
              <span className="relative flex h-2 w-2">
                {isConnected && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                )}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${isConnected ? "bg-emerald-500" : "bg-rose-500"}`}></span>
              </span>
              <span className={`font-medium ${isConnected ? "text-emerald-400" : "text-rose-400"}`}>
                {isConnected ? "Conectado" : "Desconectado"}
              </span>
            </button>
          </div>

          <div className="flex items-center justify-between font-mono text-[10px] text-slate-400 pt-1 border-t border-slate-800/60">
            <span className="text-slate-400">Versión</span>
            <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 font-semibold">v1.0.0</span>
          </div>

          <div className="flex items-center justify-between font-mono text-[10px] text-slate-400">
            <span className="text-slate-400">Last update</span>
            <span className="text-slate-300">16 Sep 2026</span>
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="px-5 py-4 border-t border-slate-800 text-[11px] text-slate-500 font-mono break-all space-y-2">
        <div>
          {cfg?.mailbox}
          <div className="mt-0.5 text-slate-600">polling &middot; {cfg?.poll_min} min</div>
        </div>
        <div className="flex gap-2 text-slate-500 font-sans">
          <button onClick={() => openLegal("privacy")} className="hover:text-slate-300 transition-colors">Privacidad</button>
          <span>&middot;</span>
          <button onClick={() => openLegal("terms")} className="hover:text-slate-300 transition-colors">Términos</button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Sidebar Desktop (Fijo >= md) */}
      <aside className="hidden md:flex w-60 shrink-0 min-h-screen sticky top-0 z-30 border-r border-slate-800 shadow-sm">
        {navContent}
      </aside>

      {/* Sidebar Mobile (Slide-over drawer < md) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex animate-in fade-in duration-200">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setMobileOpen?.(false)}
          />
          <div className="relative w-4/5 max-w-xs h-full z-10 shadow-2xl animate-in slide-in-from-left duration-200">
            {navContent}
          </div>
        </div>
      )}
      <LegalModal type={legalType} open={legalOpen} onOpenChange={setLegalOpen} />
    </>
  );
}

